import { Component, OnDestroy, OnInit, Pipe, PipeTransform, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSelectChange } from "@angular/material/select";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ConfirmationDialogComponent, ConfirmationDialogType } from "@comunicare/comunicare-dashboard-modals";
import { FileLogger, Tools } from "@comunicare/comunicare-helper";
import {
  ACTION_TARGET,
  Careplan,
  Entity,
  FHIRHelper,
  IActivity,
  IAddresses,
  ICareplan,
  ICommunication,
  ICycleSchema,
  IOrder,
  IPatientUser,
  IReference,
  IStepwiseDrug,
  ITiming,
  KeyValue,
  PRESCRIPTION_TYPE,
  STATUS_CODE,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import * as moment from "moment";
import { Subject, Subscription } from "rxjs";
import { first, map, takeUntil } from "rxjs/operators";
import { FormsData } from "src/app/helpers/formsData";
import { CareplansService } from "src/app/providers/careplans.service";
import { DrugsService } from "src/app/providers/drugs.service";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { PatientRegistrationService } from "src/app/providers/patientRegistration.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";
import { CommunicationOverviewComponent } from "../communication-overview/communication-overview.component";
import { EditAnonymousPatientComponent } from "../forms/edit-anonymous-patient/edit-anonymous-patient.component";
import { IGroup } from "../patient-careplans/patient-careplans.component";
import { CareplanActivityComponent } from "./careplan-activity/careplan-activity.component";

@Pipe({ name: "isActivityADrug" })
export class IsActivityADrugPipe implements PipeTransform {
  transform(activity: IActivity): boolean {
    return activity.detail.category.coding.some((code) => code.code === "drug");
  }
}

@Component({
  selector: "app-careplan",
  templateUrl: "./careplan.component.html",
  styleUrls: ["./careplan.component.scss"],
})
export class CareplanComponent implements OnInit, OnDestroy {
  public patient: IPatientUser;
  public careplan: ICareplan;
  public originalCareplan: ICareplan;
  private careplans: IGroup[];
  public createNewCareplan = true;
  public includeBirthdateAndGenderForm = true;
  public registration: boolean;
  private healthcareService: IReference;

  @ViewChild(EditAnonymousPatientComponent)
  initPatientComp: EditAnonymousPatientComponent;
  @ViewChildren(CareplanActivityComponent)
  activityComponents: QueryList<CareplanActivityComponent>;

  private careplanDefinitions: ICareplan[] = [];
  public pageLoaded = false;
  public careplanOriginalTemplates: ICareplan[];
  public initializedCareplanTemplates: ICareplan[];
  public templateSelectedIndex: number;
  public protocols: { [id: string]: Array<IReference> } = {};
  public routes: { [id: string]: Array<IReference> } = {};
  public administrations: { [id: string]: Array<IReference> } = {};
  public diluents: { [id: string]: Array<IReference> } = {};
  public administrators: { [id: string]: Array<IReference> } = {};
  public noCp: boolean;
  public noEndDate = false;
  public authorizedToEdit = true;
  public readOnlyWithoutDrugs = false;
  public departments: IReference[];
  public selectedDepartment: IReference = undefined;
  public timingCodes = ["rise", "morning", "noon", "evening", "bedtime"];
  public PeriodUnits = {
    d: "days",
    w: "weeks",
    M: "months",
    y: "years",
  };
  public quantities = ["1/4", "1/3", "1/2", "2/3", "3/4", "1", "2", "3", "1 + 1/4", "1 + 1/3", "1 + 1/2", "1 + 2/3", "1 + 3/4"];
  public drugCycles: { [id: string]: ICycleSchema } = {};
  public drugStepwises: { [id: string]: IStepwiseDrug } = {};
  public isLoading = true;
  public isAllServices = false;
  public hasCycle = false;
  public hasStepwise = false;
  public availablePeriodUnits: KeyValue[] = this.formsData.PERIOD_UNITS;
  private cpTranslationSub$: Subscription;
  public anyAddressActive: boolean;

  private readonly TIMING_CODE_MAP: Record<string, string> = {
    rise: Entity.TIMING_RISING,
    morning: Entity.TIMING_MORNING,
    noon: Entity.TIMING_NOON,
    evening: Entity.TIMING_EVENING,
    bedtime: Entity.TIMING_BED,
  };

  /** Subject that emits when the component has been destroyed. */
  private onDestroy$ = new Subject<void>();

  constructor(
    private careplansService: CareplansService,
    private sessionService: SessionService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private userService: UserService,
    private drugService: DrugsService,
    private dialog: MatDialog,
    private formsData: FormsData,
    private responsiveDialog: ResponsiveDialogService,
    public patientRegistrationService: PatientRegistrationService,
    private healthcareserviceService: HealthcareserviceService
  ) {}

  ngOnInit(): void {
    this.patient = this.patientRegistrationService.patientUser;
    this.careplan = this.patientRegistrationService.careplan;
    this.originalCareplan = this.careplan ? Tools.deepCopy(this.careplan) : undefined;
    this.careplans = this.patientRegistrationService.careplans;
    this.createNewCareplan = this.patientRegistrationService.careplan === undefined;
    this.includeBirthdateAndGenderForm = this.patientRegistrationService.includeBirthdateAndGenderForm;
    this.registration = this.patientRegistrationService.registration;
    this.healthcareService = this.patientRegistrationService.healthcareService;

    const patientOrgRef = this.patient?.patient?.managingOrganization.reference;
    const servicesFromPatientOrg = this.healthcareserviceService.filterServicesByOrgAndMonitoring(
      patientOrgRef,
      this.userService.allServices,
      this.sessionService.currentMonitoringOrg?.reference,
      this.sessionService.currentMonitoringService?.reference
    );
    this.departments = servicesFromPatientOrg.map((s) => s.asReference);
    if (this.createNewCareplan) {
      this.isAllServices = this.sessionService.currentService?.display === this.sessionService.allsOption;
      if (this.registration && this.healthcareService) {
        // On registration, the service has already been chosen in the previous step
        this.selectedDepartment = this.healthcareService;
      } else if (this.isAllServices) {
        this.selectedDepartment = this.departments[0];
      } else if (this.departments.find((s) => s.reference === this.sessionService.currentService.reference)) {
        this.selectedDepartment = this.sessionService.currentService;
      } else {
        this.selectedDepartment = this.departments[0];
        // Alert the user that he was trying to assign a careplan from an organization
        // in which the patient does not belong
        const trans = this.translateService.instant("careplan.selectCorrectService") + " " + this.selectedDepartment.display;
        this.snackBar.open(trans, "ok", { duration: 15000 });
      }
      this.sessionService.refreshCurrentService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        // On registration, the service is fixed: changing the current service must not change it
        if (this.registration && this.healthcareService) return;
        if (this.sessionService.currentService?.display === this.sessionService.allsOption) {
          this.isAllServices = true;
          this.selectedDepartment = this.departments[0];
          this.setupCareplanTranslations(this.selectedDepartment.reference);
        } else {
          const department = this.departments?.find((p) => p.reference === this.sessionService.currentService?.reference);
          if (department) {
            this.isAllServices = false;
            this.selectedDepartment = department;
            this.setupCareplanTranslations(this.selectedDepartment.reference);
          }
        }
      });
    } else {
      this.isAllServices = false;
      // Get the careplan's service
      const currentCareplanHealthcareservice = this.careplan.identifier.find((identifier) => identifier.system === "organization")?.use;
      // check if the user has access to the careplan's service:
      const careplanService = this.userService.allServices.find((hs) => hs.serviceRef === currentCareplanHealthcareservice)?.asReference;
      if (careplanService) {
        this.authorizedToEdit = true;
        this.selectedDepartment = careplanService;
      } else {
        this.authorizedToEdit = false;
        this.selectedDepartment = {
          reference: currentCareplanHealthcareservice,
          display: "",
        };
      }
      // Just in case:
      if (this.sessionService.isAdmin()) {
        this.authorizedToEdit = true;
      }
      // Without the right to update careplans, the page is only a read-only view of the careplan
      if (!this.userService.isAuthorizedSync(null, "careplan", "PUT")) {
        this.authorizedToEdit = false;
      }
      // Without the right to read the drugs, saving would overwrite their frequency, cycle and stepwise schema
      if (!this.canReadDrugs && this.careplan.activity?.some((activity) => this.isActivityADrug(activity))) {
        FileLogger.warn("CareplanComponent", "User does not have access to: GET dashboard/entitylink, the careplan is read-only");
        this.authorizedToEdit = false;
        this.readOnlyWithoutDrugs = true;
      }
    }

    // pre-complete the starting date
    if (this.careplan && !this.careplan.period.start) {
      this.careplan.period.start = moment().toISOString();
    }

    if (!this.careplans) {
      this.careplans = [];
    }

    this.setupCareplanTranslations(this.selectedDepartment.reference);
    this.checkPrescriptionNeeded();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private get canReadDrugs(): boolean {
    return this.userService.isAuthorizedSync(null, "dashboard/entitylink", "GET");
  }

  private async getCareplanDrugs(careplan: ICareplan): Promise<void[]> {
    if (!this.canReadDrugs) {
      return [];
    }
    const promises: Promise<void>[] = [];
    const defaultTiming: ITiming = {
      boundsPeriod: {
        start: moment().format(),
        end: moment().add(1, "months").format(),
      },
      count: null,
      frequency: 1,
      period: 1,
      periodUnits: "d",
      when: "",
      timingCode: "", // M=morning, N=noon, E=evening
    };
    for (const activity of careplan.activity) {
      if (
        this.isActivityADrug(activity) &&
        activity.detail.status === "active" &&
        activity.detail.productReference &&
        activity.detail.productReference.display
      ) {
        if (!activity.detail.scheduledTiming) {
          activity.detail.scheduledTiming = { repeat: defaultTiming };
        }
        const p = this.drugService
          .getOne(activity._id)
          .then((drug) => {
            if (drug) {
              const entityDrug = drug.entityData as any;
              activity.detail.scheduledTiming.repeat = entityDrug.frequency;
              if (entityDrug.cycle) {
                this.drugCycles[activity.reference.reference] = entityDrug.cycle;
                activity.detail.cycle = entityDrug.cycle;
              } else {
                delete activity.detail.cycle;
              }
              if (entityDrug.stepwiseSchema) {
                this.drugStepwises[activity.reference.reference] = entityDrug.stepwiseSchema;
                activity.detail.stepwise = entityDrug.stepwiseSchema;
              } else {
                delete activity.detail.stepwise;
              }
              if (entityDrug.comment) {
                activity.detail.comment = entityDrug.comment;
              } else {
                delete activity.detail.comment;
              }
              this.hasCycle = !!this.drugCycles[activity.reference.reference];
              this.hasStepwise = !!this.drugStepwises[activity.reference.reference];
            } else {
              FileLogger.warn("CareplanComponent", "No drug found for activity: ", activity.reference);
            }
          })
          .catch((err) => {
            FileLogger.error("CareplanComponent", "Error while getting careplan's drug", err);
          })
          .finally(() => {
            if (!activity.detail.scheduledTiming.repeat) {
              activity.detail.scheduledTiming.repeat = defaultTiming;
            }
          });
        promises.push(p);
      }
    }
    return await Promise.all(promises);
  }

  private setupCareplanTranslations(serviceRef: string) {
    this.cpTranslationSub$?.unsubscribe();
    if (!this.createNewCareplan && !this.careplan.templatePublicationDate) {
      FileLogger.error("setupCareplanTranslations", "this patient careplan has no templatePublicationDate", "none");
    }
    this.cpTranslationSub$ = (
      this.createNewCareplan
        ? this.careplansService.getCareplanTemplates(serviceRef, this.translateService.currentLang)
        : this.careplan.templatePublicationDate
        ? this.careplansService
            .getCareplanTemplateByPublicationDate(
              this.careplan.support[0].reference,
              this.careplan.templatePublicationDate,
              this.translateService.currentLang
            )
            .pipe(map((c) => (c ? [c] : [])))
        : // if we don't have a templatePublicationDate, then we take the active versions
          this.careplansService.getCareplanTemplates(serviceRef, this.translateService.currentLang)
    )
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((careplanDefs) => {
        if (careplanDefs.length === 0 && this.careplan) {
          FileLogger.error(
            "setupCareplanTranslations",
            `no careplantemplate found (${this.careplan.support[0].reference} - ${this.careplan.templatePublicationDate})`,
            "none"
          );
        }
        this.careplanDefinitions = careplanDefs;
        this.setupTemplates();
      });
  }

  public updateDepartment(event: MatSelectChange): void {
    this.selectedDepartment = event.value;
    this.setupCareplanTranslations(this.selectedDepartment.reference);
  }

  private setupTemplates(): void {
    this.careplanOriginalTemplates = Tools.deepCopy(this.careplanDefinitions);
    if (this.createNewCareplan) {
      this.initializedCareplanTemplates = this.initializeCareplanTemplate(this.careplanDefinitions);
      if (this.initializedCareplanTemplates.length > 0) {
        this.noCp = false;
        this.templateSelectedIndex = 0;
        this.onTemplateSelected(this.templateSelectedIndex);
      } else {
        this.careplan = undefined;
        this.noCp = true;
      }
      this.noEndDate = Careplan.hasNoEndDate(this.careplan);
      this.pageLoaded = true;
    } else {
      this.getCareplanDrugs(this.careplan).then(() => {
        this.reorderCareplanSubActivities();
        this.noEndDate = Careplan.hasNoEndDate(this.careplan);
        this.pageLoaded = true;
        this.setupIsDate();
      });
    }
    this.buildProtocols();
  }

  private buildProtocols(): void {
    this.protocols = {};
    this.routes = {};
    this.administrations = {};
    this.diluents = {};
    this.administrators = {};

    if (!this.careplan) {
      this.isLoading = false;
      return;
    }

    const currentCareplan = this.createNewCareplan
      ? this.careplan
      : this.careplanOriginalTemplates.find((cp) => cp.support[0].reference === this.careplan.support[0].reference) ?? this.careplan;

    let idxActivity = 0;
    for (const activity of currentCareplan.activity) {
      const ref = activity.reference.reference;
      if (ref) {
        this.populateDrugMaps(activity, ref);
        if (this.createNewCareplan) {
          this.initNullReferences(activity.detail, ref);
        } else {
          // Sadly we sometimes have activities with the same reference in some careplans
          // so we must check and if that's the case, we can only use the position in the array.
          const activitiesList = this.careplan.activity.filter((act) => act.reference.reference === ref);
          const currentActivity = activitiesList.length > 1 ? this.careplan.activity[idxActivity] : activitiesList[0];
          if (currentActivity?.detail) {
            this.initNullReferences(currentActivity.detail, ref);
            if (currentActivity.detail.productReference) {
              this.validateOrResetReferences(currentActivity.detail, ref);
              this.appendMissingDiluentReference(this.diluents, ref, currentActivity.detail.diluentReference);
              this.appendMissingReference(this.administrators, ref, currentActivity.detail.administratorReference);
            }
          }
        }
      }
      ++idxActivity;
    }
    this.isLoading = false;
  }

  private populateDrugMaps(activity: IActivity, ref: string): void {
    if (activity.detail.productTemplateList) this.protocols[ref] = activity.detail.productTemplateList;
    if (activity.detail.routesList) this.routes[ref] = activity.detail.routesList;
    if (activity.detail.administrationsList) this.administrations[ref] = activity.detail.administrationsList;
    if (activity.detail.diluentsList) this.diluents[ref] = activity.detail.diluentsList;
    if (activity.detail.administratorsList) this.administrators[ref] = activity.detail.administratorsList;
  }

  private initNullReferences(detail: IActivity["detail"], ref: string): void {
    const fields: [{ [id: string]: IReference[] }, string][] = [
      [this.protocols, "productReference"],
      [this.routes, "routeReference"],
      [this.administrations, "administrationReference"],
      [this.diluents, "diluentReference"],
      [this.administrators, "administratorReference"],
    ];
    for (const [map, key] of fields) {
      if (map[ref] && !(detail as any)[key]) {
        (detail as any)[key] = { display: null, reference: null };
      }
    }
  }

  private validateOrResetReferences(detail: IActivity["detail"], ref: string): void {
    const fields: [{ [id: string]: IReference[] }, string][] = [
      [this.protocols, "productReference"],
      [this.routes, "routeReference"],
      [this.administrations, "administrationReference"],
    ];
    for (const [map, key] of fields) {
      const current = (detail as any)[key] as IReference;
      if (!map[ref] || !map[ref].find((p) => p.reference === current?.reference)) {
        (detail as any)[key] = { display: null, reference: null };
      }
    }
  }

  private appendMissingReference(map: { [id: string]: IReference[] }, ref: string, reference: IReference | undefined): void {
    if (!reference?.reference) return;
    if (!map[ref]?.find((p) => p.reference === reference.reference)) {
      if (map[ref]?.length) {
        map[ref].push(reference);
      } else {
        map[ref] = [reference];
      }
    }
  }

  /**
   * Variant of appendMissingReference for diluents. Since diluentReference.display can be a
   * composed string ("qty name" or "name1 + name2"), we must not add those back to the option
   * list — doing so would make them appear as phantom selectable options in the multi-select.
   * A value is considered composed if any list item's display is a substring of it.
   * @param map The map of diluent references.
   * @param ref The reference key.
   * @param reference The diluent reference to append.
   * @returns void
   */
  private appendMissingDiluentReference(map: { [id: string]: IReference[] }, ref: string, reference: IReference | undefined): void {
    if (!reference?.reference) return;
    const list = map[ref];
    const isComposed = list?.some((p) => p.display && reference.display?.includes(p.display) && reference.display !== p.display);
    if (isComposed) return;
    if (!list?.find((p) => p.reference === reference.reference)) {
      if (list?.length) {
        list.push(reference);
      } else {
        map[ref] = [reference];
      }
    }
  }

  /**
   * Re order sub-activities based on timing
   */
  private reorderCareplanSubActivities(): void {
    for (const activity of this.careplan.activity) {
      if (activity.actionResulting) {
        activity.actionResulting = activity.actionResulting.sort((o1, o2) => o1.when?.period - o2.when?.period);
      }
    }
  }

  /**
   * select another careplan template
   */
  public onTemplateSelected(idx: number): void {
    this.templateSelectedIndex = idx;
    this.careplan = this.initializedCareplanTemplates[this.templateSelectedIndex];
    this.noEndDate = Careplan.hasNoEndDate(this.careplan);
    if (!this.noEndDate) {
      // pre-complete the end date
      if (this.careplan && !this.careplan.period.end && this.careplan.period.endTiming) {
        const { period, periodUnits } = this.careplan.period.endTiming.repeat;
        if (!period || !periodUnits) return;
        if (!this.PeriodUnits[periodUnits]) throw new Error("Invalid period unit");
        this.careplan.period.end = moment().add(period, this.PeriodUnits[periodUnits]).toISOString();
      }
    }
    this.buildProtocols();
    this.patientRegistrationService.onNeedVitalSignsChanged$.next(true);
    this.checkPrescriptionNeeded();
  }

  public isActivityADrug(activity: IActivity): boolean {
    return activity.detail.category.coding.some((code) => code.code === "drug");
  }

  /**
   * Check if careplan is valid before saving
   */
  private isCareplanValid(): boolean {
    let frequenciesValid = true;
    let hasEndlessActivity = false;

    this.activityComponents?.forEach((c) => {
      if (!c.save()) frequenciesValid = false;
    });
    if (!frequenciesValid) return false;

    const careplanStartDate = moment(this.careplan.period.start);
    const careplanEndDate = moment(this.careplan.period.end);
    if (!this.noEndDate && !careplanEndDate.isValid()) return false;
    if (!careplanStartDate.isValid()) return false;

    // check activities
    for (const activity of this.careplan.activity) {
      if (activity.detail.status !== Careplan.ACTIVITY_STATUS_INACTIVE) {
        if (this.isScheduledTiming(activity) && (this.createNewCareplan || !this.isActivityADrug(activity))) {
          const activityStartDate = moment(activity.detail.scheduledTiming.repeat.boundsPeriod.start);
          const activityEndDate = moment(activity.detail.scheduledTiming.repeat.boundsPeriod.end);
          if (
            !activityStartDate.isValid() ||
            activityStartDate.isBefore(careplanStartDate, "day") ||
            (!this.noEndDate && activityStartDate.isAfter(careplanEndDate, "day"))
          ) {
            return false;
          }
          if (activity.detail.scheduledTiming.repeat.endless === undefined || activity.detail.scheduledTiming.repeat.endless === false) {
            if (
              !activityEndDate.isValid() ||
              activityEndDate.isBefore(activityStartDate, "day") ||
              (!this.noEndDate && activityEndDate.isAfter(careplanEndDate, "day"))
            ) {
              return false;
            }
          } else {
            if (!this.noEndDate && activityStartDate.isAfter(careplanEndDate, "day")) return false;
            hasEndlessActivity = true;
          }
          if (!activity.detail.scheduledTiming.repeat.frequency) return false;
        } else if (this.isScheduledPeriod(activity)) {
          const activityStartDate = moment(activity.detail.scheduledPeriod.start);
          const activityEndDate = moment(activity.detail.scheduledPeriod.end);
          if (
            !activityStartDate.isValid() ||
            activityStartDate.isBefore(careplanStartDate, "day") ||
            (!this.noEndDate && activityStartDate.isAfter(careplanEndDate, "day"))
          ) {
            return false;
          }
          if (
            !activityEndDate.isValid() ||
            activityEndDate.isBefore(activityStartDate, "day") ||
            (!this.noEndDate && activityEndDate.isAfter(careplanEndDate, "day"))
          ) {
            return false;
          }
        } else if (this.isScheduledString(activity)) {
          const activityDate = moment(activity.detail.scheduledString);
          if (
            !activityDate.isValid() ||
            activityDate.isBefore(careplanStartDate, "day") ||
            (!this.noEndDate && activityDate.isAfter(careplanEndDate, "day"))
          ) {
            return false;
          }
        }
      }

      if (activity?.actionResulting?.length) {
        for (const action of activity.actionResulting) {
          if (action?.when?.period < -366 || action?.when?.period > 366) return false;
        }
      }
    }

    if (!hasEndlessActivity && !careplanEndDate && !this.noEndDate) return false;

    if (this.careplan?.addresses?.length && this.careplan.addresses.find((elem) => elem.status === "active") === undefined) {
      return false;
    }

    return true;
  }

  public cycleChanged(cycle: ICycleSchema): void {
    this.hasCycle = !!cycle;
  }

  public stepwiseDrugChanged(stepwise: IStepwiseDrug): void {
    this.hasStepwise = !!stepwise;
  }

  /**
   * create/update careplan
   */
  public save(): void {
    if (!this.authorizedToEdit) {
      this.patientRegistrationService.back();
      return;
    }

    if (this.noCp) {
      if (this.patientRegistrationService.isLastStep || !this.createNewCareplan) {
        this.patientRegistrationService.onFinish();
      } else {
        this.patientRegistrationService.onNextStep$.next();
      }
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    // mark form controls as touched to trigger validator on save
    this.initPatientComp?.patientForm?.markAllAsTouched();

    this.checkAnyAddressActive();

    if (!this.isCareplanValid() || (this.includeBirthdateAndGenderForm && !this.initPatientComp?.patientForm?.valid)) {
      this.snackBar.open(this.translateService.instant("error.invalidData"), undefined, {
        duration: 3000,
        horizontalPosition: "center",
        verticalPosition: "top",
      });
      this.isLoading = false;
      return;
    }
    const careplanToSave = Tools.deepCopy(this.careplan);
    // Build identifier field on careplan creation and create templatePublicationDate field
    if (this.createNewCareplan) {
      careplanToSave.templatePublicationDate = this.careplan.publicationDate;
      delete careplanToSave.publicationDate;
      careplanToSave.identifier = [
        {
          use: "usual",
          system: FHIRHelper.SYSTEM_CAREMATE,
          value: this.patient.patient.managingOrganization.reference + "/" + moment().format("X"),
        },
      ];

      careplanToSave.identifier.push({
        use: this.registration ? this.healthcareService.reference : this.selectedDepartment.reference,
        system: "organization",
        value: this.patient.patient.managingOrganization.reference,
      });
    }

    // force Moment format for start/end date and scheduled activity period
    careplanToSave.period.start = moment(this.careplan.period.start).format();
    careplanToSave.period.end = this.noEndDate ? moment(Careplan.NO_END_DATE).format() : moment(careplanToSave.period.end).format();

    careplanToSave.activity.forEach((activity) => {
      if (activity.detail.status !== Careplan.ACTIVITY_STATUS_INACTIVE) {
        if (this.isScheduledTiming(activity)) {
          activity.detail.scheduledTiming.repeat.boundsPeriod.start = moment(
            activity.detail.scheduledTiming.repeat.boundsPeriod.start
          ).format();

          if (activity.detail.scheduledTiming.repeat.boundsPeriod.end) {
            activity.detail.scheduledTiming.repeat.boundsPeriod.end = moment(
              activity.detail.scheduledTiming.repeat.boundsPeriod.end
            ).format();
          }
        } else if (this.isScheduledPeriod(activity)) {
          activity.detail.scheduledPeriod.start = moment(activity.detail.scheduledPeriod.start).format();
          activity.detail.scheduledPeriod.end = moment(activity.detail.scheduledPeriod.end).format();
        } else if (this.isScheduledString(activity)) {
          if (activity.detail.isDate || !activity.detail.scheduledString?.includes("T")) {
            activity.detail.scheduledString = moment(activity.detail.scheduledString).format("YYYY-MM-DD");
          } else {
            activity.detail.scheduledString = moment(activity.detail.scheduledString).format();
          }
        }
        if (this.drugCycles[activity.reference.reference]) {
          activity.detail.cycle = this.drugCycles[activity.reference.reference];
        } else {
          delete activity.detail.cycle;
        }
        if (this.drugStepwises[activity.reference.reference]) {
          activity.detail.stepwise = this.drugStepwises[activity.reference.reference];
        } else {
          delete activity.detail.stepwise;
        }
        if (activity.actionResulting?.length > 0) {
          activity.actionResulting.forEach((action) => {
            if (action.detail.target === ACTION_TARGET.COMMUNICATION) {
              action.detail.contentCommunication.sender = {
                reference: this.sessionService.account.caremateIdentifier,
                display: this.sessionService.account.displayName,
              };
              action.detail.contentCommunication.subjects = [
                {
                  reference: this.patient.user.caremateIdentifier,
                  display: this.patient.user.displayName
                    ? this.patient.user.displayName
                    : `${this.patient.user.name} ${this.patient.user.firstname}`,
                },
              ];
              action.detail.contentCommunication.healthservice = this.selectedDepartment;
              action.detail.contentCommunication.status = STATUS_CODE.COMPLETED;
            }
          });
        }

        if (!activity.detail.comment) {
          delete activity.detail.comment;
        }
        if (activity?.progress?.length && activity.progress[0].text) {
          activity.progress[0].authorReference = {
            reference: this.sessionService.account.caremateIdentifier,
            display: this.sessionService.account.displayName,
          };
          activity.progress[0].time = moment().toDate();
        }
      }
      if (activity.detail.productReference && !activity.detail.productReference.display) {
        delete activity.detail.productReference;
      }
    });
    //  re-compute sub-activity date base on main activity and timing
    this.computeSubActivityDates(careplanToSave);
    if (this.createNewCareplan) {
      if (this.careplans.length) {
        const allCareplans = [];
        this.careplans.forEach((group) => {
          if (group.careplans?.length) {
            allCareplans.push(...group.careplans);
          }
        });
        const alreadyExists =
          allCareplans.findIndex((careplan) => careplan.support[0].reference === careplanToSave.support[0].reference) !== -1;
        if (alreadyExists) {
          this.confirmSaveIfAlreadyExists(careplanToSave);
        } else {
          this.createOrUpdateCareplan(careplanToSave);
        }
      } else {
        this.careplansService
          .list(this.patient.user.caremateIdentifier)
          .pipe(first())
          .subscribe((careplans) => {
            if (careplans?.length) {
              const alreadyExists =
                careplans.findIndex((careplan) => careplan.support[0].reference === careplanToSave.support[0].reference) !== -1;
              if (alreadyExists) {
                this.confirmSaveIfAlreadyExists(careplanToSave);
              } else {
                this.createOrUpdateCareplan(careplanToSave);
              }
            } else {
              this.createOrUpdateCareplan(careplanToSave);
            }
          });
      }
    } else {
      this.createOrUpdateCareplan(careplanToSave);
    }
  }

  private createOrUpdateCareplan(careplanToSave: ICareplan): void {
    const apiAction = this.createNewCareplan
      ? this.careplansService.createCareplan(careplanToSave)
      : this.careplansService.updateCareplan(careplanToSave);
    apiAction.pipe(takeUntil(this.onDestroy$)).subscribe(
      (careplan) => {
        this.snackBar.open(this.translateService.instant("common.saveSuccess"), undefined, {
          duration: 3000,
          horizontalPosition: "center",
          verticalPosition: "top",
        });
        this.sessionService.needRefreshDrugsData();
        this.sessionService.needRefreshTeleconsultationsDataList();
        this.patientRegistrationService.healthcareService = this.selectedDepartment;
        this.patientRegistrationService.careplan = careplan;
        this.patientRegistrationService.onCareplanCreatedOrUpdated$.next(this.careplan.description);
      },
      (err) => {
        FileLogger.error("CareplanComponent", "createOrUpdateCareplan", err);
      },
      () => {
        // save patient gender and Birthdate
        if (this.includeBirthdateAndGenderForm) {
          this.initPatientComp?.save();
        }
        if (this.patientRegistrationService.isLastStep || !this.createNewCareplan) {
          this.patientRegistrationService.onFinish();
        } else {
          this.patientRegistrationService.onNextStep$.next();
        }
        this.isLoading = false;
      }
    );
  }

  private confirmSaveIfAlreadyExists(careplanToSave: ICareplan): void {
    const message = this.translateService
      .instant("page.careplan.alreadyExists")
      .replace(
        "%CAREPLAN_NAME%",
        this.templateSelectedIndex ? this.initializedCareplanTemplates[this.templateSelectedIndex].description : this.careplan?.description
      );
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          message,
          type: ConfirmationDialogType.CONFIRM,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((yes) => {
        if (yes) {
          this.createOrUpdateCareplan(careplanToSave);
        } else {
          this.isLoading = false;
        }
      });
  }

  /**
   * re-compute sub-activity date base on main activity and timing
   */
  private computeSubActivityDates(careplan: ICareplan): void {
    for (const activity of careplan.activity) {
      if (activity.detail.status === Careplan.ACTIVITY_STATUS_ACTIVE && activity.actionResulting) {
        for (const action of activity.actionResulting) {
          if (action.status === Careplan.ACTIVITY_STATUS_ACTIVE && action.detail.target !== ACTION_TARGET.KNOWLEDGE) {
            if (this.isScheduledString(activity)) {
              let actionDate = moment(activity.detail.scheduledString);
              actionDate = actionDate.add(action.when.period, action.when.periodUnits as moment.unitOfTime.Base);
              if (actionDate.isValid()) {
                if (action.when.boundsPeriod) {
                  action.when.boundsPeriod.start = actionDate.format();
                } else {
                  action.when.boundsPeriod = {
                    start: actionDate.format(),
                  };
                }
              }
            }
          }
        }
      }
    }
  }

  /**
   * Toggle "no end date" for careplan
   */
  public onEndlessCareplan(): void {
    if (!this.noEndDate && Careplan.hasNoEndDate(this.careplan)) {
      this.careplan.period.end = null;
    } else if (this.noEndDate) {
      this.careplan.period.end = moment(Careplan.NO_END_DATE).format();
    }
  }

  private isScheduledTiming(activity: IActivity): boolean {
    return activity.detail.scheduledTiming !== undefined;
  }

  private isScheduledPeriod(activity: IActivity): boolean {
    return activity.detail.scheduledPeriod !== undefined;
  }

  private isScheduledString(activity: IActivity): boolean {
    return activity.detail.scheduledString !== undefined;
  }

  private getActivityStartDate(activity: IActivity): moment.Moment | null {
    if (this.isScheduledTiming(activity)) return moment(activity.detail.scheduledTiming.repeat.boundsPeriod.start);
    if (this.isScheduledPeriod(activity)) return moment(activity.detail.scheduledPeriod.start);
    if (this.isScheduledString(activity)) return moment(activity.detail.scheduledString);
    return null;
  }

  private getActivityEndDate(activity: IActivity): moment.Moment | null {
    if (this.isScheduledTiming(activity)) {
      if (activity.detail.scheduledTiming.repeat.endless) return null;
      return moment(activity.detail.scheduledTiming.repeat.boundsPeriod.end);
    }
    if (this.isScheduledPeriod(activity)) return moment(activity.detail.scheduledPeriod.end);
    return null; // scheduledString has no end date
  }

  /**
   * Start date of an activity has been changed >> set careplan start date to smallest one
   */
  public onStartDateChanged(): void {
    let smallestDate: moment.Moment = null;
    for (const activity of this.careplan.activity) {
      if (activity.detail.status !== "active" || (!this.createNewCareplan && this.isActivityADrug(activity))) continue;
      const date = this.getActivityStartDate(activity);
      if (date?.isValid() && (smallestDate === null || date.isSameOrBefore(smallestDate, "day"))) {
        smallestDate = date;
      }
    }
    if (smallestDate?.isValid() && smallestDate.isBefore(this.careplan.period.start, "day")) {
      this.careplan.period.start = smallestDate.format();
    }
  }

  /**
   * End date of an activity has been changed >> set careplan end date to highest one
   */
  public onEndDateChanged(): void {
    let highestDate: moment.Moment = null;
    for (const activity of this.careplan.activity) {
      if (activity.detail.status !== "active" || (!this.createNewCareplan && this.isActivityADrug(activity))) continue;
      const date = this.getActivityEndDate(activity);
      if (date?.isValid() && (highestDate === null || date.isSameOrAfter(highestDate, "day"))) {
        highestDate = date;
      }
    }
    if (highestDate?.isValid() && highestDate.isAfter(this.careplan.period.end, "day")) {
      this.careplan.period.end = highestDate.format();
    }
  }

  public recomputeStartEndDate(): void {
    this.onStartDateChanged();
    this.onEndDateChanged();
  }

  /**
   * Switch addresses status: active/inactive
   */
  public onCheckedAddresses(addresses: IAddresses): void {
    addresses.status = addresses.status === "active" ? "inactive" : "active";
    this.checkAnyAddressActive();
  }

  private checkAnyAddressActive(): void {
    this.anyAddressActive = this.careplan?.addresses?.some((address) => address.status === "active");
  }

  public compareReference(o1: IReference, o2: IReference): boolean {
    return o1?.reference === o2?.reference;
  }

  public periodChange(activityIndex: number, actionIndex: number): void {
    const ref = this.careplan.activity[activityIndex].actionResulting[actionIndex].detail.reference;
    if (ref) {
      this.careplan.activity?.forEach((activity) => {
        activity.actionResulting?.forEach((action) => {
          if (action.detail?.reference?.reference === ref.reference) {
            action.when.period = this.careplan.activity[activityIndex].actionResulting[actionIndex].when.period;
          }
        });
      });
    }
  }

  public showCommunication(action: IOrder): void {
    const communication: ICommunication = {
      entityStatus: null,
      resourceType: null,
      identifier: null,
      basedOn: [
        {
          reference: null,
          display: null,
          type: "careplan",
        },
      ],
      partOf: null,
      inResponseTo: null,
      status: null,
      statusReason: null,
      category: null,
      priority: null,
      medium: null,
      subject: null,
      topic: {
        coding: null,
        text: action.detail.contentCommunication.topicTranslation?.[this.translateService.currentLang],
      },
      about: null,
      encounter: null,
      sent: null,
      received: null,
      recipient: null,
      sender: {
        reference: this.sessionService.account.caremateIdentifier,
        display: this.sessionService.account.displayName,
      },
      reasonCode: null,
      reasonReference: null,
      payload: [
        {
          contentString: action.detail.contentCommunication.contentTranslation?.[this.translateService.currentLang],
        },
      ],
      note: null,
      records: null,
      healthservice: this.selectedDepartment,
      id: undefined,
    };
    this.responsiveDialog.open(CommunicationOverviewComponent, {
      data: { communication },
    });
  }

  public onPeriodStartDateChanged(minDate: string, maxDate: string): void {
    let smallestDate = moment(minDate);
    const highestDate = moment(maxDate);
    if (smallestDate && highestDate && smallestDate > highestDate) {
      smallestDate = highestDate;
    }
    if (smallestDate && smallestDate.isValid()) {
      this.careplan.period.start = smallestDate.format();
    }
    this.onStartDateChanged();
  }

  public onPeriodEndDateChanged(minDate: string, maxDate: string): void {
    const smallestDate = moment(minDate);
    let highestDate = moment(maxDate);
    if (smallestDate && highestDate && highestDate < smallestDate) {
      highestDate = smallestDate;
    }
    if (highestDate && highestDate.isValid()) {
      this.careplan.period.end = highestDate.format();
    }
    this.onEndDateChanged();
  }

  private checkPrescriptionNeeded(): void {
    const isAuthorized =
      this.userService.isAuthorizedSync(null, "dashboard/link2careplan", "GET") &&
      this.userService.isAuthorizedSync(null, "dashboard/prescriptionOfficialCareplan", "GET") &&
      this.userService.isAuthorizedSync(null, "dashboard/practitionerTemplates", "GET") &&
      this.userService.isAuthorizedSync(null, "dashboard/prescription", "POST");

    if (this.careplan?.support[0].reference && isAuthorized) {
      this.careplansService
        .getLink2Careplan(this.careplan.support[0].reference)
        .pipe(first())
        .subscribe((result) => {
          const prescriptions = result.published?.prescriptions;
          let isAuthorizedToOrderDevice = true;
          if (
            prescriptions?.length > 0 &&
            prescriptions.findIndex((p) => p.prescriptionType === PRESCRIPTION_TYPE.BYTEFLIES_DEVICE) !== -1
          ) {
            isAuthorizedToOrderDevice =
              this.userService.isAuthorizedSync(null, "dashboard/devicesToOrder", "GET") &&
              this.userService.isAuthorizedSync(null, "dashboard/orderDevices", "POST");
          }
          if (isAuthorizedToOrderDevice) {
            this.patientRegistrationService.onNeedPrescriptionChanged$.next(prescriptions?.length > 0);
            this.patientRegistrationService.careplanPrescriptions = prescriptions;
          }
        });
    }
  }

  /**
   * Adapt some values in original careplan template (dates, etc.) and translate it
   */
  private initializeCareplanTemplate(defs: ICareplan[]): ICareplan[] {
    if (!defs?.length) return [];

    const arrTemplates: ICareplan[] = [];
    const today = moment().hours(8).minutes(0).seconds(0).milliseconds(0);
    defs.forEach((careplanTemplate) => {
      try {
        const template = { ...careplanTemplate };
        if (!template.period.start) {
          template.period.start = today.format();
        }
        template.subject = {
          reference: this.patient.user.caremateIdentifier,
          display: `${this.patient.user.name} ${this.patient.user.firstname}`,
        };
        template.activity.forEach((activity) => {
          if (this.isScheduledTiming(activity)) {
            activity.detail.scheduledTiming.repeat.boundsPeriod.start = null;
            if (activity.detail.scheduledTiming.repeat.boundsPeriod.end) {
              activity.detail.scheduledTiming.repeat.boundsPeriod.end = null;
            }
            if (activity.detail.scheduledTiming.repeat.quantities) {
              this.timingCodes.forEach((t) => {
                if (activity.detail.scheduledTiming.repeat.quantities[t]) {
                  if (!activity.detail.scheduledTiming.repeat.timingCode) {
                    activity.detail.scheduledTiming.repeat.timingCode = "";
                  }
                  activity.detail.scheduledTiming.repeat.timingCode += this.TIMING_CODE_MAP[t];
                }
              });
            }
          } else if (this.isScheduledPeriod(activity)) {
            activity.detail.scheduledPeriod.start = null;
            activity.detail.scheduledPeriod.end = null;
          } else if (this.isScheduledString(activity)) {
            activity.detail.isDate = activity.detail.scheduledString !== "dateTime";
          }
        });
        arrTemplates.push(template);
      } catch (err) {
        FileLogger.error("CareplanComponent", "initializeCareplanTemplate", err);
      }
    });
    return arrTemplates;
  }

  /**
   * This is called on careplan update to check if scheduledString needs a date or dateTime
   */
  private setupIsDate(): void {
    for (const activity of this.careplan.activity) {
      if (this.isScheduledString(activity)) {
        activity.detail.isDate =
          activity.detail.scheduledString !== "dateTime" &&
          !(activity.detail.scheduledString !== null && activity.detail.scheduledString.includes("T"));
      }
    }
  }
}
